import { useState, useMemo, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import { TextControl } from '../../generic/Core'
import { Icon_Folder } from '../../../styles/icons'
import * as BsIcons from 'react-icons/bs'

const ROOT = '__root__'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`

const SearchWrap = styled.div`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.border?.default || '#e5e7eb'};
  flex-shrink: 0;
`

const TreeScroll = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.5rem 0.25rem;
`

const FolderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.5rem;
  padding-left: ${({ $depth }) => `${($depth || 0) * 1.25 + 0.5}rem`};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme, $selected }) =>
    $selected ? (theme.color?.primary || '#2563eb') : (theme.font?.label || '#374151')};
  background: ${({ theme, $selected }) =>
    $selected ? (theme.dropdown?.selectedBackground || '#eff6ff') : 'transparent'};
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected
        ? (theme.dropdown?.selectedBackground || '#eff6ff')
        : (theme.dropdown?.hoverBackground || theme.area?.highBg || '#f3f4f6')};
  }

  svg { font-size: 1rem; flex-shrink: 0; opacity: 0.65; }
`

const AllRow = styled(FolderRow)`
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  border-bottom: 1px solid ${({ theme }) => theme.border?.default || '#e5e7eb'};
  border-radius: 0;
  margin-bottom: 0.25rem;
  padding-bottom: 0.5rem;
`

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 0.875rem;
  transition: transform 0.15s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  font-size: 0.65rem;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
`

const ChevronGap = styled.span`
  display: inline-block;
  width: 0.875rem;
  flex-shrink: 0;
`

const FolderPathLabel = styled.span`
  margin-left: 0.25rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EmptyMsg = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  font-size: 0.875rem;
  padding: 2rem 1rem;
  margin: 0;
`

const FolderPicker = ({ selectedId, onSelect }) => {
  const { getMe } = useApi()
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState(new Set())
  const searchRef = useRef(null)

  useEffect(() => { searchRef.current?.focus() }, [])

  const { data } = useQuery({
    queryKey: ['folder', 'tree'],
    queryFn: () => getMe('folder?noPagination=true&orderBy=path'),
    staleTime: 30000,
  })

  const folders = data?.data ?? []

  const childrenOf = useMemo(() => {
    const map = {}
    folders.forEach(f => {
      const pid = f.parentFolderId || ROOT
      if (!map[pid]) map[pid] = []
      map[pid].push(f)
    })
    return map
  }, [folders])

  const searchResults = useMemo(() => {
    if (!search.trim()) return null
    const term = search.toLowerCase()
    return folders.filter(f =>
      f.name?.toLowerCase().includes(term) ||
      f.path?.toLowerCase().includes(term)
    )
  }, [folders, search])

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const renderFolder = (folder, depth) => {
    const { id, name } = folder
    const children = childrenOf[id] || []
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(id)
    const isSelected = selectedId === id

    return (
      <div key={id}>
        <FolderRow
          $depth={depth}
          $selected={isSelected}
          onClick={() => {
            onSelect(folder)
            if (hasChildren) toggle(id)
          }}
        >
          {hasChildren
            ? <Chevron $expanded={isExpanded}><BsIcons.BsChevronRight /></Chevron>
            : <ChevronGap />
          }
          {Icon_Folder}
          {name}
        </FolderRow>
        {isExpanded && children.map(child => renderFolder(child, depth + 1))}
      </div>
    )
  }

  const roots = childrenOf[ROOT] || []

  return (
    <Wrap>
      <SearchWrap>
        <TextControl
          ref={searchRef}
          type="text"
          placeholder={t('document.folder.search.placeholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </SearchWrap>
      <TreeScroll>
        {searchResults !== null ? (
          searchResults.length === 0
            ? <EmptyMsg>{t('document.folder.empty.search')}</EmptyMsg>
            : searchResults.map(folder => (
              <FolderRow
                key={folder.id}
                $depth={0}
                $selected={selectedId === folder.id}
                onClick={() => onSelect(folder)}
              >
                <ChevronGap />
                {Icon_Folder}
                {folder.name}
                {folder.path && folder.path !== folder.name && (
                  <FolderPathLabel>{folder.path}</FolderPathLabel>
                )}
              </FolderRow>
            ))
        ) : (
          <>
            <AllRow $depth={0} $selected={selectedId === null} onClick={() => onSelect(null)}>
              <ChevronGap />
              {t('document.folder.all')}
            </AllRow>
            {roots.length === 0
              ? <EmptyMsg>{t('document.folder.empty')}</EmptyMsg>
              : roots.map(folder => renderFolder(folder, 0))
            }
          </>
        )}
      </TreeScroll>
    </Wrap>
  )
}

export default FolderPicker
